<template>
  <div id="app">
    <china-banner v-if="!isStandalonePage" />
    <header-component v-if="!isStandalonePage" />
    <main :class="isStandalonePage ? 'standalone-content' : 'main-content'">
      <router-view />
    </main>
    <footer-component v-if="!isStandalonePage" />
    <floating-buttons v-if="!isStandalonePage" />
    <cookie-consent-dialog
      v-if="showCookieConsent && !isStandalonePage"
      @accept="handleCookieConsent('accept-all')"
      @reject="handleCookieConsent('reject-all')"
      @open-settings="openCookieSettings"
    />
    <cookie-settings-dialog
      v-if="showCookieSettings && !isStandalonePage"
      @close="showCookieSettings = false"
      @save="handleCookiePreferences"
    />
  </div>
</template>

<script>
import HeaderComponent from '@/components/layout/Header.vue'
import FooterComponent from '@/components/layout/Footer.vue'
import FloatingButtons from '@/components/common/FloatingButtons.vue'
import CookieConsentDialog from '@/components/common/CookieConsentDialog.vue'
import CookieSettingsDialog from '@/components/common/CookieSettingsDialog.vue'
import ChinaBanner from '@/components/common/ChinaBanner.vue'
import { ref, onMounted, provide, computed } from 'vue'
import { isAuthenticated } from '@/api/user'
import { useRoute } from 'vue-router'

export default {
  name: 'App',
  components: {
    HeaderComponent,
    FooterComponent,
    FloatingButtons,
    CookieConsentDialog,
    CookieSettingsDialog,
    ChinaBanner
  },
  setup() {
    const route = useRoute()
    const showCookieConsent = ref(false)
    const showCookieSettings = ref(false)

    // 定义不需要显示 header 和 footer 的独立页面
    const standalonePages = ['PayVerify']

    // 判断当前是否是独立页面
    const isStandalonePage = computed(() => {
      return standalonePages.includes(route.name)
    })

    // 创建全局登录状态，并通过provide提供给所有子组件
    const globalIsLoggedIn = ref(isAuthenticated())
    provide('globalIsLoggedIn', globalIsLoggedIn)

    // 更新全局登录状态的方法
    const updateGlobalLoginStatus = () => {
      globalIsLoggedIn.value = isAuthenticated()
    }
    provide('updateGlobalLoginStatus', updateGlobalLoginStatus)

    // 检查是否已经同意过Cookie政策
    onMounted(() => {
      // 初始化时检查登录状态
      updateGlobalLoginStatus()

      const hasConsented = localStorage.getItem('cookie-consent')
      if (!hasConsented) {
        // 显示Cookie同意对话框
        showCookieConsent.value = true
      }
    })

    // 处理Cookie同意
    const handleCookieConsent = (type) => {
      if (type === 'accept-all') {
        // 接受所有Cookie
        localStorage.setItem('cookie-consent', 'all')
        localStorage.setItem('cookie-preferences', JSON.stringify({
          necessary: true,
          analytics: true,
          marketing: true,
          preferences: true
        }))
      } else if (type === 'reject-all') {
        // 拒绝所有非必要Cookie
        localStorage.setItem('cookie-consent', 'necessary')
        localStorage.setItem('cookie-preferences', JSON.stringify({
          necessary: true,
          analytics: false,
          marketing: false,
          preferences: false
        }))
      }

      // 关闭对话框
      showCookieConsent.value = false
    }

    // 打开Cookie设置对话框
    const openCookieSettings = () => {
      showCookieConsent.value = false
      showCookieSettings.value = true
    }

    // 处理Cookie偏好设置
    const handleCookiePreferences = (preferences) => {
      // 在这里可以根据用户的偏好设置来应用或删除不同类别的Cookie
      console.log('已保存Cookie偏好设置:', preferences)
    }

    return {
      isStandalonePage,
      showCookieConsent,
      showCookieSettings,
      handleCookieConsent,
      openCookieSettings,
      handleCookiePreferences,
      updateGlobalLoginStatus
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
@import '@/assets/styles/tutorial.css';

:root {
  --primary-color: #4461F2;
  --secondary-color: #38BFFF;
  --accent-color: #10B981;
  --background-color: #F7FAFC;
  --card-background: #FFFFFF;
  --text-primary: #1E293B;
  --text-secondary: #64748B;
  --text-tertiary: #94A3B8;
  --border-color: #E2E8F0;
  --hover-color: #F1F5F9;
  --shadow-sm: 0px 4px 12px rgba(15, 23, 42, 0.05);
  --shadow-md: 0px 8px 16px rgba(15, 23, 42, 0.08);
  --shadow-lg: 0px 16px 24px rgba(15, 23, 42, 0.12);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --header-height: 80px;
  --footer-height: 300px;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-primary);
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.main-content {
  min-height: calc(100vh - var(--header-height) - var(--footer-height));
  padding-top: var(--header-height);
}

.standalone-content {
  min-height: 100vh;
  padding: 0;
}

/* 当显示中国横幅时，调整主要内容区域的顶部间距 */
.china-banner + .header + .main-content {
  padding-top: calc(var(--header-height) + 40px);
}

.section {
  padding: 80px 0;
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
  color: var(--text-primary);
}

.section-subtitle {
  font-size: 18px;
  color: var(--text-secondary);
  margin-bottom: 40px;
  max-width: 600px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: var(--radius-sm);
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  cursor: pointer;
  border: none;
  outline: none;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(90deg, #4461F2 0%, #5D7BFF 100%);
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.3);
  transform: translateY(-2px);
}

.btn-secondary {
  background: var(--secondary-color);
  color: white;
}

.btn-secondary:hover {
  background: linear-gradient(90deg, #38BFFF 0%, #5DCEFF 100%);
  box-shadow: 0 4px 12px rgba(56, 191, 255, 0.3);
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  border: 2px solid var(--border-color);
  color: var(--text-primary);
}

.btn-outline:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  box-shadow: 0 4px 12px rgba(68, 97, 242, 0.1);
  transform: translateY(-2px);
}

.card {
  background-color: var(--card-background);
  border-radius: var(--radius-md);
  padding: 24px;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s;
  border: 1px solid var(--border-color);
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-size: 16px;
  transition: all 0.2s;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(55, 114, 255, 0.1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

.text-center {
  text-align: center;
}

.flex {
  display: flex;
}

.flex-col {
  flex-direction: column;
}

.items-center {
  align-items: center;
}

.justify-center {
  justify-content: center;
}

.justify-between {
  justify-content: space-between;
}

.gap-4 {
  gap: 16px;
}

.gap-6 {
  gap: 24px;
}

.grid {
  display: grid;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, 1fr);
}

.grid-cols-3 {
  grid-template-columns: repeat(3, 1fr);
}

.grid-cols-4 {
  grid-template-columns: repeat(4, 1fr);
}

@media (max-width: 1024px) {
  .grid-cols-4 {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .grid-cols-3, .grid-cols-4 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .section {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 28px;
  }
}

@media (max-width: 480px) {
  .grid-cols-2, .grid-cols-3, .grid-cols-4 {
    grid-template-columns: 1fr;
  }
  
  .btn {
    width: 100%;
  }
}
</style>
